import 'react-quill-new/dist/quill.snow.css';

import {
    forwardRef,
    type CSSProperties,
    useEffect,
    useImperativeHandle,
    useId,
    useMemo,
    useRef,
} from 'react';
import ReactQuill, { Quill as QuillNew } from 'react-quill-new';
import type QuillType from 'quill';

type EmojiMap = Record<string, string>;
const ZERO_WIDTH_SPACE = '\u200B';

let emojiBlotRegistered = false;

function registerEmojiBlot(Quill?: typeof QuillType) {
    if (!Quill) return;
    if (emojiBlotRegistered || (Quill as any).__emojiInputEmojiBlotRegistered)
        return;

    const Embed = Quill.import('blots/embed');

    class EmojiBlot extends (Embed as any) {
        static blotName = 'emoji';
        static tagName = 'IMG';
        static className = 'q-emoji';

        static create(token: string) {
            const node = super.create() as HTMLImageElement;

            node.setAttribute('data-emoji-token', token);
            node.setAttribute('alt', token);
            node.setAttribute('draggable', 'false');

            /**
             * Critical sizing rules:
             * - 1em ties emoji size to current font-size
             * - vertical-align keeps baseline consistent with native emoji
             */
            node.style.width = '1em';
            node.style.height = '1em';
            node.style.display = 'inline-block';
            node.style.verticalAlign = '-0.1em'; // tweak if needed per font

            /**
             * src is intentionally NOT trusted from user input.
             * It is applied later via refreshEmojiImageSrcs()
             * using the whitelisted emojis map.
             */
            node.setAttribute('src', '');

            return node;
        }

        static value(node: HTMLElement) {
            return node.getAttribute('data-emoji-token') || '';
        }
    }

    Quill.register(EmojiBlot, true);
    (Quill as any).__emojiInputEmojiBlotRegistered = true;
    emojiBlotRegistered = true;
}

export type EmojiInputProps = {
    emojis?: EmojiMap;
    value: string;
    onChange?: (value: string) => unknown;
    className?: string;
    onFocus?: (event: FocusEvent) => unknown;
    onKeyDown?: (event: KeyboardEvent) => unknown;

    /**
     * Optional accessible label for screen readers.
     * Defaults to "Emoji input".
     */
    ariaLabel?: string;

    /**
     * Optional placeholder text.
     */
    placeholder?: string;

    /**
     * Optional inline styles applied to the editable area.
     */
    style?: CSSProperties;

    /**
     * Remove the default Quill border.
     * Defaults to true.
     */
    borderless?: boolean;

    /**
     * Force single-line behavior (no wrapping, no scrolling).
     * Defaults to true.
     */
    singleLine?: boolean;
};

export type EmojiInputRef = {
    focus: () => void;
    blur: () => void;
    /**
     * Insert a token as emoji at the current caret position (if valid token exists in emojis map).
     */
    insertEmoji: (token: string) => void;
    /**
     * Best-effort selection helpers.
     */
    getSelection: () => { index: number; length: number } | null;
    setSelection: (index: number, length?: number) => void;
};

export const EmojiInput = forwardRef<EmojiInputRef, EmojiInputProps>(
    function EmojiInput(props, ref) {
        const {
            emojis = {},
            value,
            onChange,
            ariaLabel = 'Emoji input',
            placeholder = 'Type…',
            style,
            borderless = true,
            singleLine = true,
            className,
            onFocus,
            onKeyDown,
        } = props;

        const rqRef = useRef<ReactQuill | null>(null);
        const lastAppliedValueRef = useRef<string | null>(null);
        const lastEmojisRef = useRef<EmojiMap>(emojis);
        const isReplacingRef = useRef(false);
        const lastClassNameRef = useRef<string | null>(null);
        const placeholderStyleId = useId();
        const focusRequestedRef = useRef(false);
        const currentValueRef = useRef<string>(value);

        const ReactQuillAny = ReactQuill as any;
        const Quill: typeof QuillType | undefined =
            ReactQuillAny?.Quill ?? (QuillNew as unknown as typeof QuillType);

        const tokenRegex = useMemo(() => buildTokenRegex(emojis), [emojis]);

        registerEmojiBlot(Quill);

        // One-time setup: accessibility, keybindings, clipboard, copy behavior.
        useEffect(() => {
            const quill = getQuillInstance(rqRef.current);
            if (!quill) return;

            quill.root.setAttribute('role', 'textbox');
            quill.root.setAttribute('aria-multiline', 'false');
            quill.root.setAttribute('aria-label', ariaLabel);

            // Single-line behavior:
            quill.keyboard.addBinding({ key: 13 }, () => false);

            disableFormattingShortcuts(quill);

            setupClipboardSanitization(
                quill,
                () => lastEmojisRef.current,
                () => tokenRegex,
                () => singleLine
            );

            setupCopyAsTokens(quill);

            // Ensure initial src mapping is applied
            refreshEmojiImageSrcs(quill, lastEmojisRef.current);
        }, [ariaLabel, tokenRegex, singleLine]);

        // Forward focus/key events from the editor root.
        useEffect(() => {
            if (!onFocus && !onKeyDown) return;
            const quill = getQuillInstance(rqRef.current);
            const root = quill?.root as HTMLElement | undefined;
            if (!root) return;

            const handleFocus = (event: FocusEvent) => {
                onFocus?.(event);
            };
            const handleKeyDown = (event: KeyboardEvent) => {
                onKeyDown?.(event);
                if (singleLine) {
                    const isEnter =
                        event.key === 'Enter' ||
                        event.code === 'Enter' ||
                        (event as unknown as { keyCode?: number }).keyCode ===
                            13 ||
                        (event as unknown as { which?: number }).which === 13;
                    if (isEnter) {
                        event.preventDefault();
                        event.stopPropagation();
                    }
                }
            };

            if (onFocus) root.addEventListener('focus', handleFocus, true);
            if (onKeyDown || singleLine)
                root.addEventListener('keydown', handleKeyDown, true);

            return () => {
                if (onFocus)
                    root.removeEventListener('focus', handleFocus, true);
                if (onKeyDown || singleLine)
                    root.removeEventListener('keydown', handleKeyDown, true);
            };
        }, [onFocus, onKeyDown, singleLine]);

        // Place caret on correct side when clicking any image embed.
        useEffect(() => {
            const quill = getQuillInstance(rqRef.current);
            const root = quill?.root as HTMLElement | undefined;
            if (!root || !quill) return;

            const handleMouseDown = (event: MouseEvent) => {
                const target = event.target as HTMLElement | null;
                if (!target) return;
                const img = target.closest('img');
                if (!img || !root.contains(img)) return;

                const rect = img.getBoundingClientRect();
                const clickX = event.clientX - rect.left;
                const isLeft = clickX <= rect.width / 2;
                const blot = findBlotForNode(quill, Quill, img);
                if (!blot) return;

                const index = quill.getIndex(blot);
                quill.setSelection(isLeft ? index : index + 1, 0, 'user');
                if (quill.hasFocus && !quill.hasFocus()) quill.focus();
                event.preventDefault();
            };

            root.addEventListener('mousedown', handleMouseDown);
            return () => {
                root.removeEventListener('mousedown', handleMouseDown);
            };
        }, [Quill]);

        // Apply editor styles, className, and border preference.
        useEffect(() => {
            const quill = getQuillInstance(rqRef.current);
            if (!quill) return;

            if (borderless) {
                if (quill.container?.style)
                    quill.container.style.border = 'none';
                if (quill.root?.style) quill.root.style.border = 'none';
            }

            if (style && quill.root?.style) {
                Object.assign(quill.root.style, style);
            }

            if (singleLine && quill.root?.style) {
                quill.root.style.whiteSpace = 'nowrap';
                quill.root.style.overflowX = 'auto';
                quill.root.style.overflowY = 'hidden';
                quill.root.style.textOverflow = 'clip';
                (quill.root.style as any).scrollbarWidth = 'none';
                (quill.root.style as any).msOverflowStyle = 'none';
                const computed = window.getComputedStyle(quill.root);
                const height = parseFloat(computed.height || '0');
                const paddingTop = parseFloat(computed.paddingTop || '0');
                const paddingBottom = parseFloat(computed.paddingBottom || '0');
                const lineHeight = height - paddingTop - paddingBottom;
                if (lineHeight > 0) {
                    quill.root.style.lineHeight = `${lineHeight}px`;
                }
            }
        }, [style, borderless, singleLine]);

        useEffect(() => {
            const quill = getQuillInstance(rqRef.current);
            const root = quill?.root as HTMLElement | undefined;
            if (!root) return;

            root.setAttribute('data-emoji-input-id', placeholderStyleId);
            const styleId = `emoji-input-placeholder-${placeholderStyleId}`;
            let tag = document.getElementById(
                styleId
            ) as HTMLStyleElement | null;

            if (!tag) {
                tag = document.createElement('style');
                tag.id = styleId;
                tag.textContent = `
                    .ql-editor[data-emoji-input-id="${placeholderStyleId}"].ql-blank::before {
                        font-size: inherit;
                        font-family: inherit;
                        font-weight: inherit;
                        font-style: inherit;
                        line-height: inherit;
                        left: var(--emoji-input-placeholder-left, 15px) !important;
                    }
                    .ql-editor[data-emoji-input-id="${placeholderStyleId}"] p {
                        overflow: visible;
                        white-space: inherit;
                    }
                    .ql-editor[data-emoji-input-id="${placeholderStyleId}"] p::after {
                        content: '';
                        display: inline-block;
                        width: var(--emoji-input-right-padding, 0px);
                    }
                    .ql-editor[data-emoji-input-id="${placeholderStyleId}"] br {
                        display: none;
                    }
                    .ql-editor[data-emoji-input-id="${placeholderStyleId}"]::-webkit-scrollbar {
                        display: none;
                    }
                `;
                document.head.appendChild(tag);
            }

            return () => {
                const existing = document.getElementById(styleId);
                if (existing?.parentNode)
                    existing.parentNode.removeChild(existing);
            };
        }, [placeholderStyleId]);

        useEffect(() => {
            const quill = getQuillInstance(rqRef.current);
            const root = quill?.root as HTMLElement | undefined;
            if (!root) return;

            const apply = () => {
                const computed = window.getComputedStyle(root);
                const paddingLeft = computed.paddingLeft || '15px';
                const paddingRight = computed.paddingRight || '0px';
                root.style.setProperty(
                    '--emoji-input-placeholder-left',
                    paddingLeft
                );
                root.style.setProperty(
                    '--emoji-input-right-padding',
                    paddingRight
                );
            };

            apply();
            const raf = requestAnimationFrame(apply);
            return () => cancelAnimationFrame(raf);
        }, [style, className, placeholder, borderless, singleLine]);

        useEffect(() => {
            const quill = getQuillInstance(rqRef.current);
            const root = quill?.root as HTMLElement | undefined;
            if (!root) return;

            const prev = lastClassNameRef.current;
            if (prev) {
                for (const name of splitClassNames(prev)) {
                    root.classList.remove(name);
                }
            }

            if (className) {
                for (const name of splitClassNames(className)) {
                    root.classList.add(name);
                }
            }

            lastClassNameRef.current = className ?? null;
        }, [className]);

        useEffect(() => {
            if (!singleLine) return;
            const quill = getQuillInstance(rqRef.current);
            const root = quill?.root as HTMLElement | undefined;
            if (!root) return;

            const handler = () => {
                scheduleScrollToEnd(quill);
            };

            root.addEventListener('paste', handler);
            return () => {
                root.removeEventListener('paste', handler);
            };
        }, [singleLine]);

        useEffect(() => {
            if (!singleLine) return;
            const quill = getQuillInstance(rqRef.current);
            const root = quill?.root as HTMLElement | undefined;
            if (!root) return;

            const observer = new MutationObserver(() => {
                scheduleScrollToEnd(quill);
            });

            observer.observe(root, {
                childList: true,
                subtree: true,
                characterData: true,
            });

            return () => {
                observer.disconnect();
            };
        }, [singleLine]);

        // Keep latest emoji map available to handlers
        useEffect(() => {
            lastEmojisRef.current = emojis;
            const quill = getQuillInstance(rqRef.current);
            if (!quill) return;
            refreshEmojiImageSrcs(quill, emojis);
        }, [emojis]);

        useEffect(() => {
            currentValueRef.current = value;
        }, [value]);

        // Replace token text with emoji images as user types.
        useEffect(() => {
            const quill = getQuillInstance(rqRef.current);
            if (!quill) return;

            const handler = (_delta: any, _old: any, source: string) => {
                if (source !== 'user') return;
                if (!tokenRegex) return;
                if (isReplacingRef.current) return;

                isReplacingRef.current = true;
                const wasFocused = quill?.hasFocus?.() ?? false;
                const selectionFallback = quill.getSelection(true);
                const didReplace = replaceTokensInEditor(
                    quill,
                    lastEmojisRef.current,
                    tokenRegex,
                    selectionFallback
                );
                if (didReplace && wasFocused && selectionFallback) {
                    requestAnimationFrame(() => {
                        const len = quill.getLength();
                        quill.setSelection(
                            clamp(
                                selectionFallback.index,
                                0,
                                Math.max(0, len - 1)
                            ),
                            selectionFallback.length || 0,
                            'silent'
                        );
                        if (quill?.hasFocus && !quill.hasFocus()) {
                            quill.focus();
                        }
                    });
                }
                // Always refresh srcs (pasted embeds may have empty src).
                refreshEmojiImageSrcs(quill, lastEmojisRef.current);
                isReplacingRef.current = false;
            };

            quill.on('text-change', handler);
            return () => {
                quill.off('text-change', handler);
            };
        }, [tokenRegex]);

        // Apply controlled value into Quill while preserving selection (best-effort).
        useEffect(() => {
            const quill = getQuillInstance(rqRef.current);
            if (!quill) return;

            if (lastAppliedValueRef.current === value) return;

            const prevSelection = quill.getSelection();
            const delta = tokensToDelta(value, emojis, tokenRegex);

            quill.setContents(delta as any, 'silent');
            refreshEmojiImageSrcs(quill, emojis);

            const len = quill.getLength(); // includes trailing "\n"
            const shouldSetSelection =
                quill.hasFocus?.() || focusRequestedRef.current;
            if (shouldSetSelection) {
                if (prevSelection) {
                    quill.setSelection(
                        clamp(prevSelection.index, 0, Math.max(0, len - 1)),
                        prevSelection.length || 0,
                        'silent'
                    );
                } else {
                    quill.setSelection(Math.max(0, len - 1), 0, 'silent');
                }
                focusRequestedRef.current = false;
            }

            lastAppliedValueRef.current = value;
        }, [value, emojis, tokenRegex]);

        // Expose imperative API to parent via ref
        useImperativeHandle(
            ref,
            (): EmojiInputRef => ({
                focus() {
                    const quill = getQuillInstance(rqRef.current);
                    focusRequestedRef.current = true;
                    quill?.focus();
                },
                blur() {
                    const quill = getQuillInstance(rqRef.current);
                    quill?.blur();
                },
                insertEmoji(token: string) {
                    const quill = getQuillInstance(rqRef.current);
                    if (!quill) return;

                    const map = lastEmojisRef.current;
                    if (!token) return;

                    const range = quill.getSelection(true);
                    const index = range
                        ? range.index
                        : Math.max(0, quill.getLength() - 1);

                    if (map[token]) {
                        quill.insertEmbed(index, 'emoji', token, 'user');
                        quill.setSelection(index + 1, 0, 'user');
                        refreshEmojiImageSrcs(quill, map);
                        return;
                    }

                    quill.insertText(index, token, 'user');
                    quill.setSelection(index + token.length, 0, 'user');
                },
                getSelection() {
                    const quill = getQuillInstance(rqRef.current);
                    const sel = quill?.getSelection() ?? null;
                    return sel
                        ? { index: sel.index, length: sel.length }
                        : null;
                },
                setSelection(index: number, length = 0) {
                    const quill = getQuillInstance(rqRef.current);
                    if (!quill) return;
                    const len = quill.getLength();
                    quill.setSelection(
                        clamp(index, 0, Math.max(0, len - 1)),
                        length,
                        'user'
                    );
                },
            }),
            []
        );

        const modules = useMemo(
            () => ({
                toolbar: false,
                history: { delay: 1000, maxStack: 100, userOnly: true },
                clipboard: { matchVisual: false },
                keyboard: true,
            }),
            []
        );

        const formats = useMemo(() => ['emoji'], []);

        return (
            <ReactQuill
                ref={(r) => {
                    rqRef.current = r;
                }}
                style={{ width: '100%' }}
                theme="snow"
                modules={modules}
                formats={formats}
                placeholder={placeholder}
                onChange={(_html, _delta, source, editor) => {
                    if (source !== 'user') return;

                    const next = serializeEditorToTokens(editor);
                    lastAppliedValueRef.current = next;
                    onChange?.(next);
                }}
            />
        );
    }
);

/* ----------------------------- Helpers ----------------------------- */

function getQuillInstance(rq: ReactQuill | null): any | null {
    if (!rq) return null;
    try {
        return rq.getEditor?.() ?? null;
    } catch {
        return null;
    }
}

function findBlotForNode(
    quill: any,
    QuillCtor: typeof QuillType | undefined,
    node: Element
) {
    if (QuillCtor?.find) {
        return QuillCtor.find(node);
    }
    if (quill?.scroll?.find) {
        return quill.scroll.find(node);
    }
    return null;
}

function clamp(n: number, min: number, max: number) {
    return Math.max(min, Math.min(max, n));
}

function escapeRegExp(s: string) {
    return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function buildTokenRegex(emojis: EmojiMap): RegExp | null {
    const tokens = Object.keys(emojis || {}).filter((t) => t.length > 0);
    if (tokens.length === 0) return null;

    tokens.sort((a, b) => b.length - a.length);
    const pattern = tokens.map(escapeRegExp).join('|');
    return new RegExp(pattern, 'g');
}

function tokensToDelta(
    value: string,
    emojis: EmojiMap,
    tokenRegex: RegExp | null
) {
    const ops: Array<any> = [];

    if (!value) {
        ops.push({ insert: '\n' });
        return { ops };
    }

    if (!tokenRegex) {
        ops.push({ insert: value });
        ops.push({ insert: '\n' });
        return { ops };
    }

    tokenRegex.lastIndex = 0;

    let last = 0;
    let m: RegExpExecArray | null;
    while ((m = tokenRegex.exec(value))) {
        const start = m.index;
        const token = m[0];
        const end = start + token.length;

        if (start > last) ops.push({ insert: value.slice(last, start) });

        if (emojis[token]) ops.push({ insert: { emoji: token } });
        else ops.push({ insert: token });

        last = end;
    }

    if (last < value.length) ops.push({ insert: value.slice(last) });

    ops.push({ insert: '\n' });
    return { ops };
}

function serializeEditorToTokens(editor: any): string {
    const delta = editor.getContents();
    let out = '';

    for (const op of delta.ops || []) {
        if (typeof op.insert === 'string')
            out += op.insert.replace(/\u200B/g, '');
        else if (op.insert?.emoji) out += op.insert.emoji;
    }

    if (out.endsWith('\n')) out = out.slice(0, -1);
    return out;
}

function refreshEmojiImageSrcs(quill: any, emojis: EmojiMap) {
    const imgs = quill.root.querySelectorAll('img.q-emoji[data-emoji-token]');
    imgs.forEach((img: HTMLImageElement) => {
        const token = img.getAttribute('data-emoji-token') || '';
        const src = emojis[token];
        if (src && img.getAttribute('src') !== src)
            img.setAttribute('src', src);
        if (!src) img.setAttribute('src', '');
    });
}

function splitClassNames(input: string) {
    return input
        .split(/\s+/)
        .map((v) => v.trim())
        .filter(Boolean);
}

function scheduleScrollToEnd(quill: any) {
    setTimeout(() => {
        requestAnimationFrame(() => {
            scrollToEnd(quill);
            requestAnimationFrame(() => {
                scrollToEnd(quill);
            });
        });
    }, 0);
}

function scrollToEnd(quill: any) {
    const root = quill?.root as HTMLElement | undefined;
    if (!root) return;

    const maxRootScrollLeft = Math.max(0, root.scrollWidth - root.clientWidth);
    root.scrollLeft = maxRootScrollLeft;
    const container = quill?.container as HTMLElement | undefined;
    if (container) {
        const maxContainerScrollLeft = Math.max(
            0,
            container.scrollWidth - container.clientWidth
        );
        container.scrollLeft = maxContainerScrollLeft;
    }
}

function replaceTokensInEditor(
    quill: any,
    emojis: EmojiMap,
    tokenRegex: RegExp | null,
    selectionFallback: { index: number; length: number } | null
) {
    if (!tokenRegex) return false;

    const delta = quill.getContents();
    const replacements: Array<{
        index: number;
        length: number;
        token: string;
    }> = [];

    let index = 0;
    for (const op of delta.ops || []) {
        if (typeof op.insert === 'string') {
            const text = op.insert;
            tokenRegex.lastIndex = 0;
            let m: RegExpExecArray | null;
            while ((m = tokenRegex.exec(text))) {
                const token = m[0];
                if (emojis[token]) {
                    replacements.push({
                        index: index + m.index,
                        length: token.length,
                        token,
                    });
                }
            }
            index += text.length;
        } else {
            index += 1; // embed counts as one
        }
    }

    if (replacements.length === 0) return false;

    const selection = quill.getSelection() ?? selectionFallback;
    let nextSelectionIndex = selection ? selection.index : null;

    if (nextSelectionIndex !== null) {
        for (const rep of replacements) {
            const end = rep.index + rep.length;
            if (nextSelectionIndex >= end) {
                nextSelectionIndex -= rep.length - 1;
            } else if (
                nextSelectionIndex > rep.index &&
                nextSelectionIndex < end
            ) {
                nextSelectionIndex = rep.index + 1;
            }
        }
    }

    for (let i = replacements.length - 1; i >= 0; i -= 1) {
        const rep = replacements[i];
        quill.deleteText(rep.index, rep.length, 'silent');
        quill.insertEmbed(rep.index, 'emoji', rep.token, 'silent');
    }

    if (selection && nextSelectionIndex !== null) {
        const len = quill.getLength();
        quill.setSelection(
            clamp(nextSelectionIndex, 0, Math.max(0, len - 1)),
            selection.length || 0,
            'silent'
        );
    }

    return true;
}

function disableFormattingShortcuts(quill: any) {
    const kb = quill.keyboard;
    const block = () => false;

    kb.addBinding({ key: 'B', shortKey: true }, block);
    kb.addBinding({ key: 'I', shortKey: true }, block);
    kb.addBinding({ key: 'U', shortKey: true }, block);
    kb.addBinding({ key: 'K', shortKey: true }, block);

    kb.addBinding({ key: 9 }, block);
    kb.addBinding({ key: 9, shiftKey: true }, block);

    kb.addBinding({ key: 'S', shortKey: true, shiftKey: true }, block);
}

function setupClipboardSanitization(
    quill: any,
    getEmojis: () => EmojiMap,
    getTokenRegex: () => RegExp | null,
    getSingleLine: () => boolean
) {
    const clipboard = quill.getModule('clipboard');
    if (!clipboard) return;

    clipboard.addMatcher('IMG', (node: HTMLImageElement) => {
        const emojis = getEmojis();
        const token =
            node.getAttribute('data-emoji-token') ||
            node.getAttribute('alt') ||
            '';

        if (token && emojis[token]) {
            return { ops: [{ insert: { emoji: token } }] };
        }
        return { ops: [] }; // drop unknown images
    });

    clipboard.addMatcher(Node.TEXT_NODE, (node: any) => {
        const emojis = getEmojis();
        const re = getTokenRegex();
        let text = node?.data ?? '';
        if (!text) return { ops: [] };

        if (getSingleLine()) {
            const hadNewlines = /\r?\n/.test(text);
            text = text.replace(/\r?\n/g, '');
            if (!text && hadNewlines) {
                text = ZERO_WIDTH_SPACE;
            }
        }

        if (!re) return { ops: [{ insert: text }] };

        re.lastIndex = 0;
        const ops: Array<any> = [];
        let last = 0;
        let m: RegExpExecArray | null;

        while ((m = re.exec(text))) {
            const start = m.index;
            const token = m[0];
            const end = start + token.length;

            if (start > last) ops.push({ insert: text.slice(last, start) });

            if (emojis[token]) ops.push({ insert: { emoji: token } });
            else ops.push({ insert: token });

            last = end;
        }

        if (last < text.length) ops.push({ insert: text.slice(last) });

        return { ops };
    });
}

function setupCopyAsTokens(quill: any) {
    quill.root.addEventListener('copy', (e: ClipboardEvent) => {
        const range = quill.getSelection();
        if (!range || !e.clipboardData) return;

        const delta = quill.getContents(range.index, range.length);
        let text = '';

        for (const op of delta.ops || []) {
            if (typeof op.insert === 'string')
                text += op.insert.replace(/\u200B/g, '');
            else if (op.insert?.emoji) text += op.insert.emoji;
        }

        if (text.endsWith('\n')) text = text.slice(0, -1);

        e.clipboardData.setData('text/plain', text);
        e.preventDefault();
    });
}
